import { ConfigProvider } from 'shared/config/ConfigContext';
import ppData from 'shared/seo/data/pp.json';
import { ExtraLinkTree } from 'shared/seo/ExtraLinkTree';
import { LinkTree } from 'shared/seo/LinkTree';
import { PopularCities } from 'shared/seo/PopularCities';
import { PopularCountries } from 'shared/seo/PopularCountries';
import { DestinationPage } from '@/components/DestinationPage';
import { countryLevel1Factory, prepackageSearchFactory } from '@/components/factory';
import { feConfig } from '@/config';
import { getCountriesForDestination, prepackageCountries, prepackageMapping } from '@/data';
import { verticals } from '@/data/verticals';
import type { CountryPageProps, SeoPageContext } from '@/interfaces/pages';

export async function getStaticPaths() {
    const destinations = prepackageCountries.map((place) => ({
        params: { countryLevel1: place.countryPath },
    }));

    return {
        paths: destinations,
        fallback: false,
    };
}

export async function getStaticProps(context: SeoPageContext) {
    const destination = prepackageCountries.find((place) => place.countryPath === context.params.countryLevel1.toLowerCase());

    const popularCities = prepackageMapping[destination.id] ?? [];
    const popularCountries = getCountriesForDestination(destination, prepackageCountries);

    return {
        props: {
            destination,
            popularCities,
            popularCountries,
            config: feConfig,
        },
    };
}

const CountryLevel1 = ({ destination, popularCities, popularCountries, config }: CountryPageProps) => {
    const factory = countryLevel1Factory(destination, prepackageSearchFactory());

    return (
        <ConfigProvider config={config}>
            <DestinationPage factory={factory} vertical={verticals.prepackage} data={ppData}>
                <PopularCities
                    destination={destination}
                    cities={popularCities}
                    cdnUrl={ppData.cdnPath}
                    verticalUrl={ppData.basePath}
                    lang={ppData.lang}
                />
                <PopularCountries
                    destination={destination}
                    countries={popularCountries}
                    cdnUrl={ppData.cdnPath}
                    verticalUrl={ppData.basePath}
                    lang={ppData.lang}
                />
                <LinkTree
                    heading={ppData.lang.linkTree.heading}
                    description={ppData.lang.linkTree.description}
                    data={ppData.linkTree}
                    url={ppData.basePath}
                />
                <ExtraLinkTree
                    heading={ppData.lang.extraLinkTree.heading}
                    linkPrefix={ppData.lang.extraLinkTree.prefix}
                    data={ppData.extraLinkTree}
                    url={ppData.basePath}
                />
            </DestinationPage>
        </ConfigProvider>
    );
};

export default CountryLevel1;
